import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldGroup } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
import { SwitchField } from "./FormFields";
import { SectionHeading } from "./SectionLayout";
import { PLACEMENT_FIELDS, type PlacementField } from "./sponsored-brands-model";

interface BiddingStrategyFieldsProps {
  automatedBidding: boolean;
  onAutomatedBiddingChange: (automated: boolean) => void;
  placementAdjustmentsEnabled: boolean;
  onPlacementAdjustmentsEnabledChange: (enabled: boolean) => void;
  placementValues: Record<PlacementField, string>;
  onPlacementValueChange: (key: PlacementField, value: string) => void;
}

export function BiddingStrategyFields({
  automatedBidding,
  onAutomatedBiddingChange,
  placementAdjustmentsEnabled,
  onPlacementAdjustmentsEnabledChange,
  placementValues,
  onPlacementValueChange,
}: BiddingStrategyFieldsProps) {
  const { t } = useTranslation("campaignCreation");
  // Audience adjustments stay off: the public SB contract lacks the audience IDs they need.
  const audienceBidding = false;
  return (
    <>
      <SectionHeading title={t("sb.bid.title")} />
      <FieldGroup data-slot="sb-bid-fields" className="max-w-[462px] gap-4">
        <SwitchField
          checked={automatedBidding}
          onCheckedChange={onAutomatedBiddingChange}
          label={t("sb.bid.automated")}
          description={t("sb.bid.automatedDescription")}
        />
        {!automatedBidding ? (
          <PlacementAdjustments
            enabled={placementAdjustmentsEnabled}
            onEnabledChange={onPlacementAdjustmentsEnabledChange}
            values={placementValues}
            onValueChange={onPlacementValueChange}
          />
        ) : null}
        <SwitchField
          checked={audienceBidding}
          disabled
          label={t("sb.bid.audience")}
          description={t("sb.bid.audienceDescription")}
          blocker={t("sb.blockers.audienceData")}
        />
      </FieldGroup>
    </>
  );
}

function PlacementAdjustments({
  enabled,
  onEnabledChange,
  values,
  onValueChange,
}: {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  values: Record<PlacementField, string>;
  onValueChange: (key: PlacementField, value: string) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [touched, setTouched] = useState<Partial<Record<PlacementField, boolean>>>({});
  const [selectionTouched, setSelectionTouched] = useState(false);

  const errorFor = (key: PlacementField) => {
    if (!enabled || !touched[key]) return null;
    const value = values[key];
    if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 900) {
      return t("sb.bid.placement.validation");
    }
    return null;
  };

  return (
    <div className="max-w-[640px] space-y-1">
      <div
        data-slot="sb-placement-adjustments"
        className={cn(
          "rounded-lg border px-6 py-5 transition-colors",
          enabled ? "border-primary" : "border-border",
        )}
      >
        <div className="mb-5 flex items-center gap-3 font-medium text-foreground">
          <Checkbox
            id="sb-placement-adjustments-enabled"
            aria-label={t("sb.bid.placement.label")}
            checked={enabled}
            onCheckedChange={(checked) => {
              setSelectionTouched(true);
              onEnabledChange(checked);
              if (!checked) setTouched({});
            }}
          />
          <label htmlFor="sb-placement-adjustments-enabled" className="cursor-pointer">
            {t("sb.bid.placement.label")}
          </label>
        </div>

        <div className="space-y-5">
          {PLACEMENT_FIELDS.map(({ key }) => {
            const error = errorFor(key);
            const inputId = `sb-placement-${key}`;
            return (
              <div key={key} className="grid grid-cols-[minmax(0,1fr)_12rem] items-start gap-6">
                <label htmlFor={inputId} className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">
                    {t(`sb.bid.placement.${key}.title`)}
                  </span>
                  <span className="mt-1 block text-sm leading-5 text-muted-foreground">
                    {t(`sb.bid.placement.${key}.description`)}
                  </span>
                </label>
                <Field data-invalid={error ? true : undefined} className="gap-1.5">
                  <InputGroup>
                    <InputGroupInput
                      id={inputId}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={900}
                      step={1}
                      placeholder="0 ~ 900"
                      className="[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      value={values[key]}
                      disabled={!enabled}
                      aria-label={t(`sb.bid.placement.${key}.title`)}
                      aria-invalid={error ? true : undefined}
                      onChange={(event) => onValueChange(key, event.target.value)}
                      onBlur={() =>
                        setTouched((current) => ({
                          ...current,
                          [key]: true,
                        }))
                      }
                    />
                    <InputGroupAddon
                      align="inline-end"
                      className="min-w-12 border-s border-control-border px-3"
                    >
                      <InputGroupText>%</InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                  {error ? (
                    <FieldDescription className="text-destructive-foreground">
                      {error}
                    </FieldDescription>
                  ) : null}
                </Field>
              </div>
            );
          })}
          <div className="grid grid-cols-[minmax(0,1fr)_12rem] gap-6">
            <span aria-hidden="true" />
            <p className="text-sm leading-5 text-muted-foreground">
              {t("sb.bid.placement.example")}
            </p>
          </div>
        </div>
      </div>
      {selectionTouched && !enabled ? (
        <p className="text-sm text-destructive-foreground">
          {t("sb.bid.placement.selectionRequired")}
        </p>
      ) : null}
    </div>
  );
}
